
import Vue from "vue";
import Component from "vue-class-component";
import { Splitpanes, Pane } from "splitpanes";
import PrimaryButton from "@/vue/components/PrimaryButton.vue";
import BriefingMatrix from "@/vue/components/BriefingMatrix.vue";
import BriefingPage from "@/vue/components/BriefingPage.vue";
import BriefingDialogue from "@/vue/components/BriefingDialogue.vue";
import BriefingCloneDialogue from "@/vue/components/BriefingCloneDialogue.vue";
import PreviewLinksDialogue from "@/vue/components/PreviewLinksDialogue.vue";
import ChoosePathwayDialogue from "@/vue/components/ChoosePathwayDialogue.vue";
import Utils from "@/utilities/Utils";
import ApiClient from "@/utilities/ApiClient";
import { previewVisitID } from "@/model/Enums";
import { ClientSearchParameters } from "@/model/ClientSearchParameters";
import { SiteSearchParameters } from "@/model/SiteSearchParameters";
import { IClient, Client, IClientLookup } from "@/model/Client";
import { ISite, ISiteLookup, Site } from "@/model/Site";
import { IBriefing, Briefing } from "@/model/Briefing";
import { IMapplicOption, MapplicOption } from "@/model/MapplicOption";

@Component({
    components: { Splitpanes, Pane, PrimaryButton, BriefingMatrix, BriefingPage, BriefingDialogue, BriefingCloneDialogue, PreviewLinksDialogue, ChoosePathwayDialogue }
})
export default class PagePart extends Vue {

    async mounted(): Promise<void> {
        window.addEventListener("resize", this.onResize);
        this.onResize();

        this.briefingID = this.$route.params.briefingID ?? Utils.emptyGuidValue;

        await Promise.all([
            (async () => { this.clientList = await ApiClient.get("/api/client/lookups"); })(),
            (async () => { this.siteList = await ApiClient.get("/api/site/lookups"); })(),
            //(async () => { await ApiClient.get(`/api/site/mapplicOptions?siteID=${}`).then((r: Array<IMapplicOption>) => this.mapplicOptionList = r.map(i => new MapplicOption(i))) })(),
            this.loadClient(),
            this.loadSite(),
            this.loadBriefing(),
        ]);

        await ApiClient.get(`/api/site/mapplicOptions?siteID=${this.site.id}`)
            .then((r: Array<IMapplicOption>) => this.mapplicOptionList = r.map(i => new MapplicOption(i)));

        console.log("this.mapplicOptionList = ", this.mapplicOptionList);
    }

    beforeDestroy(): void {
        window.removeEventListener("resize", this.onResize);
    }

    contentContainerTop: number = 176;

    clientList: Array<IClientLookup> = [];
    siteList: Array<ISiteLookup> = [];
    mapplicOptionList: Array<IMapplicOption> = [];
    briefingID: string = Utils.emptyGuidValue;
    client: Client = new Client();
    site: Site = new Site();
    briefing: Briefing = new Briefing();
    selectedPageID: string = "";
    get canClone(): boolean { return !Utils.isEmptyId(this.briefing.id); }

    async loadClient(): Promise<void> {
        const parameters = new ClientSearchParameters();
        parameters.briefingID = this.briefingID;
        const data: { count: number; list: Array<IClient> } = await ApiClient.post("/api/client/search", parameters);
        if (data.count > 0) this.client.update(data.list[0]);
        else Utils.resetObject(this.client);
    }

    async loadSite(): Promise<void> {
        const parameters = new SiteSearchParameters();
        parameters.briefingID = this.briefingID;
        const data: { count: number; list: Array<ISite> } = await ApiClient.post("/api/site/search", parameters);
        if (data.count > 0) this.site.update(data.list[0]);
        else Utils.resetObject(this.site);
    }

    async loadBriefing(): Promise<void> {
        const data: IBriefing = await ApiClient.get(`/api/briefing/load?id=${this.briefingID}`);
        this.briefing.update(data);
        console.log("Loaded briefing - ", this.briefing);
    }

    //
    // --
    //

    onResize(): void {
        const contentContainer: HTMLElement = (this.$refs.splitpanes as Vue)?.$el as HTMLElement;
        const rect = contentContainer?.getBoundingClientRect();
        this.contentContainerTop = (rect?.top ?? 176) + 1;
    }

    getLinks(): void {
        const dialog: PreviewLinksDialogue = this.$refs.previewLinksDialogue as PreviewLinksDialogue;
        dialog.show(this.briefingID);
    }

    choosePathwayForPrint(): void {
        const dialog: ChoosePathwayDialogue = this.$refs.choosePathwayForPrintDialogue as ChoosePathwayDialogue;
        dialog.show(this.briefing);
    }

    onPathwaySelectedForPrint(pathwayID: string): void {
        const url = `/briefingPrint/${previewVisitID}?previewPathwayID=${pathwayID}`;
        const printWindow = window.open(url);
        if(printWindow == null) {
            alert("Failed to open pop-up print window.  Do you have a pop-up blocker?");
            return;
        }
        //Close window once print is finished
        printWindow.onafterprint = () =>{
            printWindow.close();
        };
    }

    confirmClone(): void {
        const dialog: BriefingCloneDialogue = this.$refs.briefingCloneDialogue as BriefingCloneDialogue;
        dialog.show(this.briefing);
    }

    onBriefingCloned(briefingID: string): void {
        this.$router.push({ name: "AdminBriefing", params: { briefingID: briefingID } });
    }

    editBriefing(): void {
        const dialog: BriefingDialogue = this.$refs.briefingDialogue as BriefingDialogue;
        dialog.edit(this.briefingID);
    }

    onBriefingSaved(briefing: IBriefing): void {
        this.briefing.update(briefing);
        console.log("updated briefing = ", briefing);
        // was it new?
        if(Utils.isEmptyId(this.briefingID)) {
            this.briefingID = briefing.id;
            let url = `/admin/briefing/${this.briefingID}`;
            history.pushState({}, document.title, url);
        }
    }

    onBriefingDeleted(): void {
        this.$router.push({ name: "AdminBriefings" });
    }

    // event from matrix
    onPageAdd(isQuiz: boolean, sectionID: string, displayOrder: number): void {
        if(Utils.isEmptyId(this.briefing.id)) return;

        this.selectedPageID = Utils.emptyGuidValue; // Fires BriefingPage PageID watcher!
        const briefingPage: BriefingPage = this.$refs.briefingPage as BriefingPage;
        //const matrix: BriefingMatrix = this.$refs.briefingMatrix as BriefingMatrix;
        briefingPage.newPage(isQuiz, sectionID, displayOrder);
        // after save, "page-saved" event fires - see this.onPageSaved
    }

    // event from matrix
    onPageEdit(pageID: string): void {
        this.selectedPageID = pageID;
    }

    // event from briefingPage
    onPageSaved(pageID: string): void {
        this.selectedPageID = pageID;
        const matrix: BriefingMatrix = this.$refs.briefingMatrix as BriefingMatrix;
        matrix.load();
    }

    // will deselct and also trigger "isNoAction" in BriefingPage.vue via prop
    onPageReset(): void {
        this.selectedPageID = "";
    }
}
